import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { qk } from '../api/queries'
import { fetchAllMarketplaceListings, useToolCategories } from '../api/catalog'
import type { ConnectedAccountsResponse, ConnectionsResponse, ToolCategory } from '../api/types'
import { useSession } from '../context/SessionContext'
import { Icon } from '../components/Icon'
import { Modal } from '../components/Modal'
import { formatDate } from '../components/format'
import { Badge, EmptyState, PageHeader, QueryState } from '../components/ui'
import {
  DEFAULT_FILTERS,
  STATE_BADGE,
  applyFilters,
  buildToolkitRows,
  categoryLabel,
  categoryOptions,
  type AuthType,
  type SortKey,
  type ToolkitFilters,
  type ToolkitRow,
} from './toolkits'
import { ConnectModal } from './toolkits/ConnectModal'

const PROVIDER_LABEL: Record<NonNullable<ToolkitRow['provider']>, string> = {
  official: 'Symone managed',
  partner: 'Partner',
  community: 'Community',
}

// icon_url is the listing's logo (marketplace_listings.logo_url). A broken image
// falls back to the initial so the row never shows a broken-image glyph.
function AppIcon({ row }: { row: ToolkitRow }) {
  const [failed, setFailed] = useState(false)
  if (row.iconUrl && !failed) {
    return <img className="app-icon" src={row.iconUrl} alt="" loading="lazy" onError={() => setFailed(true)} />
  }
  return <span className="app-icon app-icon--fallback" aria-hidden="true">{row.name.slice(0, 1)}</span>
}

const ACTION_LABEL: Record<ToolkitRow['state'], string> = {
  connected: 'Manage',
  needs_reconnect: 'Reconnect',
  pending_config: 'Finish setup',
  deploying: 'Manage',
  unhealthy: 'Fix',
  inactive: 'Turn on',
  not_connected: 'Connect',
}

function StubModal({ kind, onClose }: { kind: 'custom' | 'request'; onClose: () => void }) {
  return (
    <Modal
      title={kind === 'custom' ? 'Custom Toolkit' : 'Request a toolkit'}
      onClose={onClose}
      footer={<button type="button" className="btn btn--primary" onClick={onClose}>Got it</button>}
    >
      {kind === 'custom' ? (
        <>
          <p>
            Custom toolkits will let you turn any HTTP API into governed tools. Point Symone at an
            <strong> OpenAPI 3.x</strong> spec (URL or upload) and it will generate one tool per
            operation, with input schemas taken from the spec’s parameters and request bodies.
          </p>
          <ul className="soon-list">
            <li><Icon name="check" size={14} /> Choose which operations to expose</li>
            <li><Icon name="check" size={14} /> Attach an auth config (API key or OAuth2)</li>
            <li><Icon name="check" size={14} /> Workspace policies apply like any other toolkit</li>
          </ul>
          <p className="muted">OpenAPI ingest is a platform-admin operation today; ask your Symone contact to add a spec.</p>
        </>
      ) : (
        <p>
          Toolkit requests aren’t wired up yet. Tell your Symone contact which app and actions you
          need and we’ll prioritise it.
        </p>
      )}
    </Modal>
  )
}

function Rail({
  rows,
  vocab,
  filters,
  setFilters,
  onRequest,
}: {
  rows: ToolkitRow[]
  vocab: ToolCategory[]
  filters: ToolkitFilters
  setFilters: (f: ToolkitFilters) => void
  onRequest: () => void
}) {
  const [open, setOpen] = useState(false)
  const cats = categoryOptions(rows, vocab)
  const set = <K extends keyof ToolkitFilters>(k: K, v: ToolkitFilters[K]) => setFilters({ ...filters, [k]: v })

  return (
    <aside className="rail" aria-label="Toolkit filters">
      <div className="input-icon">
        <Icon name="search" size={14} />
        <input
          type="search"
          aria-label="Search toolkits"
          placeholder="Search toolkits"
          value={filters.search}
          onChange={(e) => set('search', e.target.value)}
        />
      </div>

      <div className="rail-section">
        <button
          type="button"
          className="rail-toggle"
          aria-expanded={open}
          aria-controls="toolkit-filter-panel"
          onClick={() => setOpen((v) => !v)}
        >
          <Icon name="filter" size={14} /> Filter &amp; Sort
          <Icon name={open ? 'chevronDown' : 'chevron'} size={14} className="push-right" />
        </button>
        {open && (
          <div id="toolkit-filter-panel" className="rail-panel">
            <label className="field field--compact">
              <span>Sort</span>
              <select value={filters.sort} onChange={(e) => set('sort', e.target.value as SortKey)}>
                <option value="name">Name (A–Z)</option>
                <option value="popular">Most installed</option>
                <option value="recent">Recently updated</option>
              </select>
            </label>
            <label className="field field--compact">
              <span>Type</span>
              <select
                value={filters.provider}
                onChange={(e) => set('provider', e.target.value as ToolkitFilters['provider'])}
              >
                <option value="all">All types</option>
                <option value="official">Symone managed</option>
                <option value="partner">Partner</option>
                <option value="community">Community</option>
              </select>
            </label>
            <label className="field field--compact">
              <span>Auth type</span>
              <select value={filters.auth} onChange={(e) => set('auth', e.target.value as 'all' | AuthType)}>
                <option value="all">Any auth</option>
                <option value="OAuth2">OAuth2</option>
                <option value="API Key">API Key</option>
                <option value="No auth">No auth</option>
              </select>
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={filters.connectedOnly}
                onChange={(e) => set('connectedOnly', e.target.checked)}
              />
              Connected only
            </label>
            <label className="check check--disabled" title="Needs trigger capability metadata from the gateway">
              <input type="checkbox" disabled /> Supports triggers <Badge tone="info">Soon</Badge>
            </label>
          </div>
        )}
      </div>

      <div className="rail-section">
        <h2 className="rail-heading">Categories</h2>
        <ul className="cat-list">
          <li>
            <button
              type="button"
              className={'cat' + (filters.category === null ? ' active' : '')}
              onClick={() => set('category', null)}
            >
              All <span className="cat-count">{rows.length}</span>
            </button>
          </li>
          {cats.map((c) => (
            <li key={c.slug}>
              <button
                type="button"
                className={'cat' + (filters.category === c.slug ? ' active' : '')}
                aria-pressed={filters.category === c.slug}
                onClick={() => set('category', filters.category === c.slug ? null : c.slug)}
              >
                {c.label} <span className="cat-count">{c.count}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      <button type="button" className="link-btn" onClick={onRequest}>
        Request toolkit <Icon name="chevron" size={12} />
      </button>
    </aside>
  )
}

export function ToolkitsView() {
  const { workspaceId } = useSession()
  // ?search=<toolkit> deep link (e.g. from the Playground's "connect first" panel).
  // ?connect=<toolkit> opens that toolkit's connect dialog (from the Playground's hints).
  const [params, setParams] = useSearchParams()
  const [filters, setFilters] = useState<ToolkitFilters>(() => ({
    ...DEFAULT_FILTERS,
    search: params.get('search') ?? '',
    category: params.get('category') || null,
  }))
  const [stub, setStub] = useState<'custom' | 'request' | null>(null)
  const [connectKey, setConnectKey] = useState<string | null>(() => params.get('connect'))
  const { categories: vocab } = useToolCategories()

  const market = useQuery({
    queryKey: qk.marketplace(workspaceId),
    queryFn: fetchAllMarketplaceListings,
  })
  // Connections only add "connected" state; a failure here must not hide the catalog.
  const conns = useQuery({
    queryKey: qk.connections(workspaceId),
    queryFn: () => apiFetch<ConnectionsResponse>('/connections'),
    retry: false,
  })

  // The caller's own OAuth / API-key accounts (per-user state for OAuth and vendor MCP toolkits).
  const accounts = useQuery({
    queryKey: qk.connectedAccounts(workspaceId),
    queryFn: () => apiFetch<ConnectedAccountsResponse>('/connected-accounts'),
    retry: false,
  })
  const rows = useMemo(
    () =>
      buildToolkitRows(market.data ?? [], conns.data?.connections ?? [], accounts.data?.accounts ?? []),
    [market.data, conns.data, accounts.data],
  )
  const visible = useMemo(() => applyFilters(rows, filters), [rows, filters])
  const connectRow = connectKey ? rows.find((r) => r.key === connectKey) ?? null : null
  const closeConnect = () => {
    setConnectKey(null)
    if (params.has('connect')) {
      const next = new URLSearchParams(params)
      next.delete('connect')
      setParams(next, { replace: true })
    }
  }

  return (
    <div className="page">
      <PageHeader
        title="Toolkits"
        count={market.data ? rows.length : undefined}
        description="Apps your agents can use through Symone. Connect one to make its tools available."
        actions={
          <button type="button" className="btn" onClick={() => setStub('custom')}>
            <Icon name="plus" size={14} /> Custom Toolkit
          </button>
        }
      />

      <QueryState isLoading={market.isLoading} error={market.error} what="the toolkit catalog" onRetry={() => market.refetch()}>
        {rows.length === 0 ? (
          <EmptyState
            icon="grid"
            title="No toolkits in the catalog yet"
            body="When toolkits are published to this gateway’s marketplace they’ll appear here, ready to connect."
            sampleHeaders={['App', 'Categories', 'Auth', 'Status', 'Last updated']}
            sampleRows={[
              ['Calendar app', 'Scheduling', <Badge>OAuth2</Badge>, <Badge tone="success">Connected</Badge>, 'Oct 1, 2026'],
              ['Issue tracker', 'Dev', <Badge>API Key</Badge>, <Badge>Not connected</Badge>, 'Sep 18, 2026'],
            ]}
          />
        ) : (
          <div className="with-rail">
            <div className="table-wrap">
              {visible.length === 0 ? (
                <div className="state-panel state-panel--quiet">
                  <p>No toolkits match these filters.</p>
                  <button type="button" className="btn btn--sm" onClick={() => setFilters(DEFAULT_FILTERS)}>
                    Clear filters
                  </button>
                </div>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>App</th>
                      <th>Categories</th>
                      <th>Auth</th>
                      <th>Status</th>
                      <th>Management</th>
                      <th>Last updated</th>
                      <th><span className="sr-only">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((r) => (
                      <tr key={r.key}>
                        <td>
                          <div className="app-cell">
                            <AppIcon row={r} />
                            <div>
                              <div className="cell-strong">{r.name}</div>
                              <div className="slug-list cell-sub">
                                {r.slugs.map((s) => <code key={s}>{s}</code>)}
                              </div>
                              <div className="cell-sub">
                                {r.slugs.length} {r.slugs.length === 1 ? 'variant' : 'variants'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="badge-row" aria-label={`${r.name} categories`}>
                            {r.categories.length === 0 ? (
                              <span className="muted">—</span>
                            ) : (
                              r.categories.map((c) => (
                                <button
                                  key={c}
                                  type="button"
                                  className="cat-chip"
                                  title={`Show only ${categoryLabel(c, vocab)}`}
                                  onClick={() => setFilters({ ...filters, category: c })}
                                >
                                  {categoryLabel(c, vocab)}
                                </button>
                              ))
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="badge-row">
                            {r.auth.map((a) => <Badge key={a}>{a}</Badge>)}
                          </div>
                        </td>
                        <td>
                          <Badge tone={STATE_BADGE[r.state].tone}>{STATE_BADGE[r.state].label}</Badge>
                        </td>
                        <td>{r.provider ? PROVIDER_LABEL[r.provider] ?? r.provider : <span className="muted">—</span>}</td>
                        <td>{formatDate(r.updated)}</td>
                        <td className="cell-actions">
                          <button
                            type="button"
                            className={r.state === 'not_connected' || r.state === 'connected' ? 'btn btn--sm' : 'btn btn--sm btn--primary'}
                            aria-label={`${ACTION_LABEL[r.state]} ${r.name}`}
                            onClick={() => setConnectKey(r.key)}
                          >
                            {ACTION_LABEL[r.state]}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <Rail rows={rows} vocab={vocab} filters={filters} setFilters={setFilters} onRequest={() => setStub('request')} />
          </div>
        )}
      </QueryState>

      {stub && <StubModal kind={stub} onClose={() => setStub(null)} />}
      {connectRow && <ConnectModal row={connectRow} onClose={closeConnect} />}
    </div>
  )
}
